import React, { useState } from "react";
import {
  Box,
  Tabs,
  Text,
  createStyles,
  Group,
  Select,
  Card,
  Avatar,
  Badge,
  Notification,
  Button,
  Stack,
  Paper,
  SimpleGrid,
} from "@mantine/core";
import {
  IconUserCheck,
  IconCalendarEvent,
  IconUsers,
  IconBell,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { DateRangePicker } from "@mantine/dates";
import { useGetUser } from "api/users/getUser";
import { DirectorStats } from "./components/DirectorStats";
import { DirectorAttendanceTable } from "./components/DirectorAttendanceTable";
import { DirectorLeaveTable } from "./components/DirectorLeaveTable";
import { DirectorTeamMembers } from "./components/DirecotorTeamMembers";
import { DirectorNotifications } from "./components/DirecotorNotifications";

const useStyles = createStyles((theme) => ({
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: theme.spacing.lg,
    color: theme.colorScheme === "dark" ? theme.white : theme.black,
  },
  tabLabel: {
    fontWeight: 600,
    fontSize: 15,
  },
  teamCard: {
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows.md,
    },
  },
  notificationItem: {
    borderLeft: `3px solid ${theme.colors.blue[6]}`,
    marginBottom: theme.spacing.sm,
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[5]
          : theme.colors.gray[0],
    },
  },
  filterContainer: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    marginBottom: "20px",
  },
  tabList: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  tabGroup: {
    display: "flex",
    gap: 16,
  },
}));

export default function DirectorDashboard() {
  const { classes } = useStyles();
  const { data: user } = useGetUser();
  const [activeTab, setActiveTab] = useState<string | null>("attendance");
  const [filterType, setFilterType] = useState<string>("Today");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  // Determine user role for display
  let roleDisplay = "Team";
  if (user?.role === "director") {
    roleDisplay = "Director";
  } else if (user?.role === "supervisor") {
    roleDisplay = "Supervisor";
  }

  const handleWidgetClick = (type: "attendance" | "leaves") => {
    setActiveTab(type);
  };

  // Function to get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "present":
        return "green";
      case "on leave":
        return "yellow";
      case "late":
        return "red";
      default:
        return "gray";
    }
  };

  // Function to get notification icon based on type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "leave":
        return <IconCalendarEvent size={18} />;
      case "attendance":
        return <IconUserCheck size={18} />;
      default:
        return <IconBell size={18} />;
    }
  };

  return (
    <Box>
      <Group position="apart" mb="xl">
        <div>
          <Text className={classes.title}>{roleDisplay} Dashboard</Text>
          <Text color="dimmed" size="sm" mt={-15}>
            Manage attendance and leaves for your{" "}
            {user?.department?.name || "team"}
          </Text>
        </div>
      </Group>

      {/* Widgets Section */}
      <DirectorStats
        onWidgetClick={handleWidgetClick}
        activeTab={activeTab as "attendance" | "leaves"}
      />

      <Tabs
        value={activeTab}
        onTabChange={setActiveTab}
        variant="outline"
        radius="md"
        mt="xl"
      >
        <Tabs.List mb="lg" className={classes.tabList}>
          <div className={classes.tabGroup}>
            <Tabs.Tab
              mb="md"
              value="attendance"
              icon={<IconUserCheck size={16} />}
              className={classes.tabLabel}
            >
              Team Attendance
            </Tabs.Tab>
            <Tabs.Tab
              mb="md"
              value="leaves"
              icon={<IconCalendarEvent size={16} />}
              className={classes.tabLabel}
            >
              Leave Requests
            </Tabs.Tab>
            <Tabs.Tab
              mb="md"
              value="team"
              icon={<IconUsers size={16} />}
              className={classes.tabLabel}
            >
              Team Members
            </Tabs.Tab>
            <Tabs.Tab
              mb="md"
              value="notifications"
              icon={<IconBell size={16} />}
              className={classes.tabLabel}
            >
              Notifications
            </Tabs.Tab>
          </div>

          {/* Date Filter Controls - Only show for attendance and leaves tabs */}
          {(activeTab === "attendance" || activeTab === "leaves") && (
            <div className={classes.filterContainer}>
              <Select
                label="Filter Type"
                aria-label="Filter Type"
                data={[
                  { value: "Today", label: "Today" },
                  { value: "Yesterday", label: "Yesterday" },
                  { value: "Last 7 Days", label: "Last 7 Days" },
                  { value: "Last 30 Days", label: "Last 30 Days" },
                  { value: "Custom Range", label: "Custom Range" },
                ]}
                value={filterType}
                onChange={(v) => {
                  setFilterType(v || "Today");
                  if (v !== "Custom Range") {
                    setDateRange([null, null]);
                  }
                }}
                style={{ minWidth: 150 }}
              />

              {filterType === "Custom Range" && (
                <DateRangePicker
                  label="Date Range"
                  aria-label="Date Range"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={(v) => {
                    setDateRange(v as [Date | null, Date | null]);
                  }}
                  clearable
                />
              )}
            </div>
          )}
        </Tabs.List>

        <Tabs.Panel value="attendance" pt="xs">
          <DirectorAttendanceTable
            filterType={filterType}
            dateRange={dateRange}
          />
        </Tabs.Panel>

        <Tabs.Panel value="leaves" pt="xs">
          <DirectorLeaveTable filterType={filterType} dateRange={dateRange} />
        </Tabs.Panel>

        <Tabs.Panel value="team" pt="xs">
          <DirectorTeamMembers />
        </Tabs.Panel>

        <Tabs.Panel value="notifications" pt="xs">
          <DirectorNotifications />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}
